import React, { useState, useEffect, useRef } from "react";
import API_BASE from "../config/api";
import Swal from "sweetalert2";

// ---- TOASTER ALERT FUNCTION ----
const showToast = (message, icon) => {
    Swal.fire({
        toast: true,
        position: "top-end",
        icon: icon,
        title: message,
        showConfirmButton: false,
        timer: 2000,
        customClass: {
            popup:
                icon === "success"
                    ? "my-toast my-toast-success"
                    : icon === "error"
                        ? "my-toast my-toast-error"
                        : "my-toast my-toast-warning",
        },
    });
};

export default function WebHome() {

    const user = JSON.parse(localStorage.getItem("user"));
    const listinguid = localStorage.getItem("listinguid");

    const [formData, setFormData] = useState({
        sitename: "",
        slogan: "",
        coverimg: "",
        youtube_link: ""
    });

    // New: browse-from-device support for the hero image, alongside the
    // existing "paste a URL" field. Mirrors the upload pattern WebAboutMe /
    // WebPortfolio already use (a real File sent as multipart, field name
    // "profilepics" — same convention the backend already expects there).
    // NOTE: save_webhome.php needs to accept a "profilepics" file part and,
    // when present, upload it and store/return that path instead of (or in
    // addition to) the plain coverimg URL — same as save_webabout.php does.
    const [coverFile, setCoverFile] = useState(null);
    const [coverPreview, setCoverPreview] = useState("");
    const fileInputRef = useRef(null);

    const [loading, setLoading] = useState(true);

    // ===========================
    // LOAD EXISTING DATA
    // ===========================
    useEffect(() => {
        fetch(`${API_BASE}/api/get_webhome.php?personaluid=${user.personaluid}&listinguid=${listinguid}`)
            .then(res => res.json())
            .then(data => {
                if (data.success && data.data) {
                    setFormData({
                        sitename: data.data.name ?? "",
                        slogan: data.data.slogan ?? "",
                        coverimg: data.data.coverimg ?? "",
                        youtube_link: data.data.youtube_link ?? ""
                    });

                }
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
                showToast("Failed to load data", "error");
            });
    }, []);

    // Revoke the local blob preview URL whenever it's replaced/unmounted,
    // otherwise each new file picked leaks the previous preview's memory.
    useEffect(() => {
        return () => {
            if (coverPreview) URL.revokeObjectURL(coverPreview);
        };
    }, [coverPreview]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleBrowseClick = () => {
        fileInputRef.current?.click();
    };

    const handleCoverFileChange = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        if (coverPreview) URL.revokeObjectURL(coverPreview);
        setCoverFile(file);
        setCoverPreview(URL.createObjectURL(file));
        // A freshly-browsed file takes priority over a pasted URL — clear
        // the URL field so it's obvious which source will actually be saved.
        setFormData((s) => ({ ...s, coverimg: "" }));
    };

    const handleRemoveCover = () => {
        if (coverPreview) URL.revokeObjectURL(coverPreview);
        setCoverFile(null);
        setCoverPreview("");
        if (fileInputRef.current) fileInputRef.current.value = "";
        setFormData((s) => ({ ...s, coverimg: "" }));
    };

    // ===========================
    // SAVE DATA WITH TOASTER
    // ===========================
    const handleSubmit = (e) => {
        e.preventDefault();

        const yt = (formData.youtube_link || "").trim();
        if (yt && !/^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\//i.test(yt)) {
            showToast("YouTube field must be a valid YouTube link", "error");
            return;
        }

        const fd = new FormData();
        fd.append("personaluid", user.personaluid);
        fd.append("fullname", user.fullname);
        fd.append("listinguid", listinguid);
        fd.append("sitename", formData.sitename);
        fd.append("slogan", formData.slogan);
        fd.append("coverimg", formData.coverimg);
        fd.append("youtube_link", yt);
        if (coverFile) fd.append("profilepics", coverFile);

        fetch(`${API_BASE}/api/save_webhome.php`, {
            method: "POST",
            body: fd
        })
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    showToast(data.message, "success");
                } else {
                    showToast(data.message, "error");
                }
            })
            .catch(() => {
                showToast("Something went wrong", "error");
            });
    };

    if (loading) return <div className="p-4 text-secondary">Loading...</div>;

    const previewSrc = coverPreview || formData.coverimg;

    return (
        <div className="container-fluid px-4 py-3">

            <div
                className="rounded p-3 mb-3 shadow-sm"
                style={{
                    background: "linear-gradient(135deg, #e8f0ff, #fefefe)",
                    borderLeft: "6px solid #4a90e2"
                }}
            >
                <h5 className="fw-bold text-dark mb-1">Website – Home</h5>
                <p className="text-secondary mb-0">Update your website contents.</p>
            </div>

            <div className="card shadow-sm border-0 mb-4">
                <div className="card-body">

                    <form autoComplete="off" onSubmit={handleSubmit}>

                        <div className="row">
                            <div className="col-md-5 mb-3">
                                <label className="form-label fw-semibold">
                                    Website Name <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="sitename"
                                    className="form-control form-control-sm"
                                    value={formData.sitename}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="col-md-7 mb-3">
                                <label className="form-label fw-semibold">
                                    Slogan <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="slogan"
                                    className="form-control form-control-sm"
                                    value={formData.slogan}
                                    onChange={handleChange}
                                />
                            </div>

                            {/* BACKGROUND / HERO IMAGE — browse & upload, or paste a URL */}
                            <div className="col-md-12 mb-3">
                                <label className="form-label fw-semibold">
                                    Background / Hero Image
                                </label>

                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    className="d-none"
                                    onChange={handleCoverFileChange}
                                />

                                {previewSrc ? (
                                    <div
                                        className="border rounded d-flex align-items-center gap-3 p-2"
                                        style={{ background: "#f8f9fa" }}
                                    >
                                        <img
                                            src={previewSrc}
                                            alt="Background preview"
                                            className="rounded"
                                            style={{ width: 140, height: 90, objectFit: "cover", flexShrink: 0 }}
                                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                                        />
                                        <div className="flex-grow-1 small text-secondary text-truncate">
                                            {coverFile ? coverFile.name : formData.coverimg}
                                        </div>
                                        <div className="d-flex gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={handleBrowseClick}
                                            >
                                                <i className="bi bi-arrow-repeat me-1" /> Change
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={handleRemoveCover}
                                            >
                                                <i className="bi bi-trash" />
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        onClick={handleBrowseClick}
                                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleBrowseClick(); }}
                                        className="border border-2 border-dashed rounded d-flex flex-column align-items-center justify-content-center text-center py-4"
                                        style={{ cursor: "pointer", background: "#f8f9fa" }}
                                    >
                                        <i className="bi bi-image fs-2 text-secondary mb-2" />
                                        <div className="fw-semibold small">Click to browse an image</div>
                                        <div className="text-secondary" style={{ fontSize: 12 }}>JPG, PNG or WEBP — wide 1920×1080 recommended</div>
                                    </div>
                                )}

                                <div className="mt-2">
                                    <label className="form-label small text-secondary mb-1">Or paste an image URL instead</label>
                                    <input
                                        type="text"
                                        name="coverimg"
                                        className="form-control form-control-sm"
                                        placeholder="https://your-image-url.jpg"
                                        value={formData.coverimg}
                                        onChange={(e) => {
                                            // Typing a URL supersedes a previously browsed file.
                                            if (coverFile) {
                                                if (coverPreview) URL.revokeObjectURL(coverPreview);
                                                setCoverFile(null);
                                                setCoverPreview("");
                                                if (fileInputRef.current) fileInputRef.current.value = "";
                                            }
                                            handleChange(e);
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="col-md-12 mb-3">
                                <label className="form-label fw-semibold">
                                    YouTube Video Link
                                </label>
                                <input
                                    type="text"
                                    name="youtube_link"
                                    className="form-control form-control-sm"
                                    placeholder="https://www.youtube.com/watch?v=..."
                                    value={formData.youtube_link}
                                    onChange={handleChange}
                                />
                                <div className="form-text">Only one YouTube link is shown on your website — a link, not an uploaded video file.</div>
                            </div>

                        </div>

                        <div className="text-end">
                            <button type="submit" className="btn btn-primary px-4 mt-2">
                                <i className="bi bi-cloud-upload-fill me-1"></i>
                                Update
                            </button>
                        </div>

                    </form>

                </div>
            </div>

        </div>
    );
}